/**
 * USE WORK ITEM TYPES HOOK
 *
 * Client-side hook that fetches work item type definitions from the
 * /api/v1/work-item-types endpoint and caches them in a module-level
 * singleton so all components share the same data without redundant fetches.
 *
 * Provides helpers for looking up type metadata (label, icon, color)
 * by type_key, replacing scattered hardcoded maps.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import * as LucideIcons from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { authService } from '@/lib/authService';

const LUCIDE_ICON_MAP = LucideIcons as unknown as Record<string, LucideIcon>;

export interface WorkItemTypeMeta {
  type_key: string;
  type_label: string;
  type_label_plural: string;
  category: string;
  description: string;
  icon: string;
  color: string;
  enabled: boolean;
}

interface WorkItemTypesResponse {
  success: boolean;
  types: WorkItemTypeMeta[];
  enabled_types: string[];
}

// ─── Module-level cache (shared across all hook instances) ───────────────────

let cachedTypes: WorkItemTypeMeta[] | null = null;
let cachedTypeMap: Map<string, WorkItemTypeMeta> | null = null;
let fetchPromise: Promise<WorkItemTypeMeta[]> | null = null;

async function fetchWorkItemTypes(): Promise<WorkItemTypeMeta[]> {
  if (cachedTypes) {
    return cachedTypes;
  }

  if (fetchPromise) {
    return fetchPromise;
  }

  fetchPromise = (async () => {
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) {
        return [];
      }

      const response = await fetch('/api/v1/work-item-types', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return [];
      }

      const data: WorkItemTypesResponse = await response.json();
      const types = Array.isArray(data.types) ? data.types : [];

      cachedTypes = types;
      cachedTypeMap = new Map(types.map((t) => [t.type_key, t]));

      return types;
    } catch {
      return [];
    } finally {
      fetchPromise = null;
    }
  })();

  return fetchPromise;
}

// ─── Icon resolution ──────────────────────────────────────────────────────────

export function resolveLucideIcon(iconName?: string | null): LucideIcon | null {
  if (!iconName) return null;

  const raw = iconName.trim();
  if (!raw) return null;

  const pascalCase = raw
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');

  const candidates = [
    raw,
    raw.charAt(0).toUpperCase() + raw.slice(1),
    pascalCase,
  ];

  for (const candidate of candidates) {
    const icon = LUCIDE_ICON_MAP[candidate];
    if (icon) {
      return icon;
    }
  }

  return null;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface UseWorkItemTypesResult {
  types: WorkItemTypeMeta[];
  typeMap: Map<string, WorkItemTypeMeta>;
  loading: boolean;
  getTypeMeta: (typeKey: string) => WorkItemTypeMeta | null;
  getTypeLabel: (typeKey: string) => string;
  getTypeLabelPlural: (typeKey: string) => string;
  getTypeIcon: (typeKey: string) => LucideIcon | null;
  getTypeColor: (typeKey: string) => string;
  isTypeEnabled: (typeKey: string) => boolean;
  refresh: () => Promise<void>;
}

export function useWorkItemTypes(): UseWorkItemTypesResult {
  const [types, setTypes] = useState<WorkItemTypeMeta[]>(() => cachedTypes ?? []);
  const [loading, setLoading] = useState(() => !cachedTypes);

  useEffect(() => {
    if (cachedTypes) {
      return;
    }

    let cancelled = false;
    fetchWorkItemTypes().then((result) => {
      if (!cancelled) {
        setTypes(result);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const typeMap = useMemo(() => {
    if (cachedTypeMap) {
      return cachedTypeMap;
    }
    return new Map(types.map((t) => [t.type_key, t]));
  }, [types]);

  const getTypeMeta = useCallback(
    (typeKey: string): WorkItemTypeMeta | null => {
      return typeMap.get(typeKey) || null;
    },
    [typeMap]
  );

  const getTypeLabel = useCallback(
    (typeKey: string): string => {
      const meta = typeMap.get(typeKey);
      return meta?.type_label || humanizeItemType(typeKey);
    },
    [typeMap]
  );

  const getTypeLabelPlural = useCallback(
    (typeKey: string): string => {
      const meta = typeMap.get(typeKey);
      return meta?.type_label_plural || meta?.type_label || humanizeItemType(typeKey);
    },
    [typeMap]
  );

  const getTypeIcon = useCallback(
    (typeKey: string): LucideIcon | null => {
      const meta = typeMap.get(typeKey);
      return resolveLucideIcon(meta?.icon);
    },
    [typeMap]
  );

  const getTypeColor = useCallback(
    (typeKey: string): string => {
      const meta = typeMap.get(typeKey);
      return meta?.color || '#3b82f6';
    },
    [typeMap]
  );

  const isTypeEnabled = useCallback(
    (typeKey: string): boolean => {
      const meta = typeMap.get(typeKey);
      return meta?.enabled ?? true;
    },
    [typeMap]
  );

  const refresh = useCallback(async () => {
    cachedTypes = null;
    cachedTypeMap = null;
    setLoading(true);
    const result = await fetchWorkItemTypes();
    setTypes(result);
    setLoading(false);
  }, []);

  return {
    types,
    typeMap,
    loading,
    getTypeMeta,
    getTypeLabel,
    getTypeLabelPlural,
    getTypeIcon,
    getTypeColor,
    isTypeEnabled,
    refresh,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function humanizeItemType(itemType: string): string {
  return itemType
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export default useWorkItemTypes;
